import { useEffect, useState } from 'react';
import { checkPasswordPolicy, MIN_PASSWORD_LENGTH } from '@lbhl/shared';
import {
  ApiError,
  OfflineError,
  enroll,
  fetchAuthConfig,
  login,
  setToken,
  type AuthConfig,
} from '../lib/api.js';

/**
 * Sign in, or enroll with a one-time code.
 *
 * Two jobs on one screen because they are the same question asked of two people:
 * someone who already has a password, and someone holding an invitation. Splitting
 * them across routes would mean an invitee following a link to a form that tells
 * them their password is wrong.
 *
 * Every state below describes what is actually true of the server rather than
 * rendering a control that cannot work. A dead button teaches people the tool is
 * broken; a sentence tells them what to do.
 */
type Mode = 'password' | 'enroll';

type ConfigState =
  | { kind: 'loading' }
  | { kind: 'ready'; config: AuthConfig }
  /** The API could not be reached, but the browser believes it is online. */
  | { kind: 'unreachable' }
  /** The browser itself reports no connection. */
  | { kind: 'offline' };

export function SignIn({
  message,
  onSignedIn,
  onCancel,
}: {
  message?: string | undefined;
  onSignedIn(): Promise<void>;
  onCancel?: (() => void) | undefined;
}) {
  const [state, setState] = useState<ConfigState>({ kind: 'loading' });
  const [mode, setMode] = useState<Mode>('password');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState(() => codeFromUrl());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // An enrollment link carries its code in the fragment, so arriving by one should
  // land on the enrollment form rather than a password prompt.
  useEffect(() => {
    if (codeFromUrl()) setMode('enroll');
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!navigator.onLine) {
        setState({ kind: 'offline' });
        return;
      }
      try {
        const config = await fetchAuthConfig();
        if (!cancelled) setState({ kind: 'ready', config });
      } catch {
        // Deliberately distinct from 'offline'. "You have no signal" and "the
        // server is not answering" need different responses from the person
        // reading it, and conflating them sends them to check the wrong thing.
        if (!cancelled) setState({ kind: navigator.onLine ? 'unreachable' : 'offline' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const problems = mode === 'enroll' && password ? checkPasswordPolicy(password) : [];

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const session =
        mode === 'password'
          ? await login(email, password)
          : await enroll({ email, enrollmentCode: code, password });
      // The existing token is only replaced once a new one is actually in hand.
      setToken(session.token);
      await onSignedIn();
    } catch (err) {
      setError(describeAuthError(err, mode));
    } finally {
      setBusy(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <Shell>
        <p className="muted">Checking with the server…</p>
      </Shell>
    );
  }

  if (state.kind === 'offline') {
    return (
      <Shell message={message} onCancel={onCancel}>
        <div className="note warn">
          <strong>Signing in needs a connection.</strong> Your password is checked by the
          server, so this is the one thing that cannot be done offline. Any bids you have
          already built are saved on this device and will sync once you are back online.
        </div>
      </Shell>
    );
  }

  if (state.kind === 'unreachable') {
    return (
      <Shell message={message} onCancel={onCancel}>
        <div className="note bad">
          <strong>The server is not responding.</strong> Your connection looks fine, so this
          is the bid tool itself rather than your signal. Try again shortly; if it persists,
          whoever runs the server needs to know.
        </div>
      </Shell>
    );
  }

  const { config } = state;

  return (
    <Shell message={message} onCancel={onCancel}>
      <div className="note">
        Access is <strong>invite-only</strong>.
      </div>

      {/* Labels are explicitly associated: tapping one focuses its field, which
          matters most on a phone, and a screen reader announces it. */}
      <div className="field">
        <label htmlFor="signin-email">Email</label>
        <input
          id="signin-email"
          type="email"
          inputMode="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {mode === 'enroll' && (
        <div className="field">
          <label htmlFor="signin-code">Enrollment code</label>
          <input
            id="signin-code"
            type="text"
            // Codes are uppercase base32; a phone keyboard autocapitalising or
            // autocorrecting them causes failures that look like a wrong code.
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {/* Six characters, and spacing or case is ignored — worth saying, because
              every refusal here reads the same, so someone whose correct code was
              rejected over a stray space would have nothing to go on. */}
          <p className="muted">
            The code from whoever invited you. Capitals and spacing do not matter.
          </p>
        </div>
      )}

      <div className="field">
        <label htmlFor="signin-password">
          {mode === 'enroll' ? 'Choose a password' : 'Password'}
        </label>
        <input
          id="signin-password"
          type="password"
          autoComplete={mode === 'enroll' ? 'new-password' : 'current-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && canSubmit() && void submit()}
        />
        {mode === 'enroll' && (
          <p className="muted">
            At least {MIN_PASSWORD_LENGTH} characters. A few unrelated words is the easiest
            way to get there, and far harder to guess than a short one with symbols in it.
          </p>
        )}
      </div>

      {/* The same rule the server enforces, run here so nobody submits and waits to
          be told. See packages/shared/src/password.ts. */}
      {problems.length > 0 && (
        <div className="note warn">
          {problems.map((p) => (
            <div key={p.message}>{p.message}</div>
          ))}
        </div>
      )}

      {error && <div className="note bad">{error}</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="primary" disabled={!canSubmit() || busy} onClick={() => void submit()}>
          {busy ? 'Working…' : mode === 'enroll' ? 'Enroll' : 'Sign in'}
        </button>
        <button
          className="ghost"
          onClick={() => {
            setMode(mode === 'password' ? 'enroll' : 'password');
            setError(null);
          }}
        >
          {mode === 'password' ? 'I have an enrollment code' : 'I already have a password'}
        </button>
      </div>

      {config.googleClientId && (
        <GoogleButton clientId={config.googleClientId} onSignedIn={onSignedIn} />
      )}
    </Shell>
  );

  function canSubmit(): boolean {
    if (!email || !password) return false;
    if (mode === 'enroll') return code.trim().length > 0 && problems.length === 0;
    return true;
  }
}

function Shell({
  children,
  message,
  onCancel,
}: {
  children: React.ReactNode;
  message?: string | undefined;
  onCancel?: (() => void) | undefined;
}) {
  return (
    <div className="wrap">
      <section className="card" style={{ marginTop: 48 }}>
        <h2>Sign in</h2>
        {message && <div className="note warn">{message}</div>}
        {children}
        {/* Rendered for EVERY state, not just the form. Without an escape, asking to
            sign in while offline is a dead end: an installed PWA has no address bar
            or reload button to get back with, and clearing site data — the only other
            way out — deletes the queued bids. */}
        {onCancel && (
          <div style={{ marginTop: 14 }}>
            <button className="ghost" onClick={onCancel}>
              Back to bids
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Turns an error from the auth endpoints into something the person can act on.
 *
 * The server answers every identity failure identically and on purpose, so this
 * cannot say more than the server does — it can only say it in a way that suggests
 * the next step.
 */
function describeAuthError(err: unknown, mode: Mode): string {
  if (err instanceof OfflineError) {
    return 'No connection. You can keep building bids; they will sync once you are back online.';
  }
  if (err instanceof ApiError) {
    if (err.status === 429) return err.message;
    if (err.status === 400) return err.message;
    if (err.status === 401 || err.status === 403) {
      return mode === 'enroll'
        ? 'That address and code do not match an open invitation. Codes expire after a week — ' +
            'ask whoever invited you to issue a new one.'
        : 'That email address and password do not match an active account. If you have never ' +
            'set a password, use the enrollment code you were given.';
    }
    return err.message;
  }
  return (err as Error).message;
}

/** Reads `#code=…` from an enrollment link. A fragment never reaches the server. */
function codeFromUrl(): string {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    return new URLSearchParams(hash).get('code') ?? '';
  } catch {
    return '';
  }
}

/**
 * Google Sign-In, rendered only when the server reports a client ID.
 *
 * Untested against a real client: there is no Google Cloud Console access, so no
 * OAuth client exists yet. It is here because the seam costs nothing to keep open,
 * and nothing renders at all until a client ID is configured.
 */
function GoogleButton({
  clientId,
  onSignedIn,
}: {
  clientId: string;
  onSignedIn(): Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        await loadGis();
        if (cancelled) return;
        const google = (window as unknown as { google?: any }).google;
        if (!google?.accounts?.id) return;

        google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: { credential?: string }) => {
            if (!response.credential) return;
            try {
              const { googleLogin } = await import('../lib/api.js');
              const session = await googleLogin(response.credential);
              setToken(session.token);
              await onSignedIn();
            } catch (err) {
              setError(describeAuthError(err, 'password'));
            }
          },
        });
        const host = document.getElementById('google-button');
        if (host) google.accounts.id.renderButton(host, { theme: 'outline', size: 'large' });
      } catch {
        // A blocked or unreachable Google script is not an error worth shouting
        // about: the password form above still works.
      }
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [clientId, onSignedIn]);

  return (
    <div style={{ marginTop: 18 }}>
      <p className="muted">or</p>
      <div id="google-button" />
      {error && <div className="note bad">{error}</div>}
    </div>
  );
}

/** Loads Google Identity Services once, on demand. */
let gisPromise: Promise<void> | null = null;

function loadGis(): Promise<void> {
  gisPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Google Sign-In.'));
    document.head.appendChild(script);
  });
  return gisPromise;
}
